export const flatCategories = (categoryToFlat) => {
    if (!categoryToFlat.children?.length) {
        return categoryToFlat.slug ? { [categoryToFlat.slug]: categoryToFlat } : {};
    }
    return categoryToFlat.children.reduce((result, current) => {
        let topCategory = {};
        if (current.slug) {
            topCategory = {
                [current.slug]: current,
            };
        }
        return { ...topCategory, ...result, ...flatCategories(current) };
    }, {});
};
export const createFilters = (attributes, routeQuery) => {
    return attributes
        .filter((atr) => !!(atr.options && atr.options.length > 0))
        .map((atr) => {
        const options = atr.options
            ? atr.options.map((option) => option)
            : [];
        return {
            ...atr,
            options,
        };
    })
        .map((filter) => {
        if (!filter.options) {
            return { ...filter, options: [] };
        }
        const options = filter.options.map((option) => {
            const filterFromRoute = routeQuery[filter.name];
            return {
                ...option,
                selected: filterFromRoute &&
                    (filterFromRoute === option.value ||
                        filterFromRoute.includes(option.value)),
            };
        });
        return {
            ...filter,
            options,
        };
    });
};
export const flatOptions = (filters) => {
    return filters.reduce((result, current) => {
        const options = current.options.map((option) => {
            return {
                ...option,
                systemKey: current.systemKey,
                filterName: current.name,
                attributeUuid: current.uuid,
            };
        });
        return [...result, ...options];
    }, []);
};
export const createParams = (filters) => {
    const flattenOptions = flatOptions(filters);
    return flattenOptions
        .filter((option) => option.selected)
        .reduce((result, option) => {
        const current = 'attributes';
        return {
            ...result,
            [current]: result[current]
                ? {
                    ...result[current],
                    [option.attributeUuid]: result[current][option.attributeUuid]
                        ? [...result[current][option.attributeUuid], option.uuid]
                        : [option.uuid],
                }
                : { [option.attributeUuid]: [option.uuid] },
        };
    }, {});
};
export const createQueryParamsString = (filters) => {
    const options = flatOptions(filters).filter((option) => option.selected);
    return options.reduce((queryParams, current) => {
        return queryParams
            ? `${queryParams}&${current.filterName}=${current.value}`
            : `?${current.filterName}=${current.value}`;
    }, '');
};
const norm = (v) => String(v ?? "").trim().toLowerCase();
const matchesSegment = (node, segment) => !!node && (norm(node.slug) === norm(segment) || norm(node.title) === norm(segment));
/**
 * Find the root node for the first segment.
 * 1) tries exact object key match: treeObj[firstSegment]
 * 2) scans values by slug/title match
 */
function getRootNode(treeObj, firstSegment) {
    const byKey = treeObj[firstSegment];
    if (byKey)
        return byKey;
    for (const key in treeObj) {
        const node = treeObj[key];
        if (matchesSegment(node, firstSegment))
            return node;
    }
    return null;
}
/**
 * Walks a path like ["destinations","korea","tours-experiences", ...]
 * through the catalog tree.
 *
 * - Matches segments against node.slug OR node.title (case-insensitive).
 * - Also tolerates matches where child's fullSlugPath ends with the joined path so far.
 * - If resolution fails mid-way, returns the deepest partial match in `chain` and `node: null`.
 */
export function findByPath(treeObj, pathSegments) {
    if (!treeObj || !Array.isArray(pathSegments) || pathSegments.length === 0) {
        return { node: null, chain: [], root: null };
    }
    let current = getRootNode(treeObj, pathSegments[0]);
    if (!current)
        return { node: null, chain: [], root: null };
    const chain = [current];
    for (let i = 1; i < pathSegments.length; i++) {
        if (current.children.length === 0) {
            return { node: null, chain, root: chain[0] };
        }
        const seg = pathSegments[i];
        const kids = Array.isArray(current.children) ? current.children : [];
        // Prefer slug/title match
        let next = kids.find(k => matchesSegment(k, seg));
        // Fallback: match by fullSlugPath ending
        if (!next) {
            const targetEnd = norm(pathSegments.slice(0, i + 1).join("/"));
            next = kids.find(k => norm(k.fullSlugPath).endsWith(targetEnd));
        }
        if (!next) {
            return { node: null, chain, root: chain[0] };
        }
        chain.push(next);
        current = next;
    }
    return { node: current, chain, root: chain[0] };
}
